import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { ProductCard } from "@itaas/pep-contracts";
import { getProducts } from "../api/pep-api";
import { SiteLayout } from "../components/layout/SiteLayout";
import { SeoHead } from "../components/seo/SeoHead";
import { labelCategory, labelStatus, productDisplayName, statusBadgeClass } from "../utils/labels";

export function ComparePage() {
  const [products, setProducts] = useState<ProductCard[]>([]);
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    getProducts().then(setProducts).catch(console.error);
  }, []);

  const toggle = (slug: string) => {
    setSelected((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : prev.length < 4 ? [...prev, slug] : prev,
    );
  };

  const compared = products.filter((p) => selected.includes(p.slug));

  return (
    <SiteLayout>
      <SeoHead
        title="Comparateur de modules"
        description="Comparez les modules ITaaS — fonctionnalités, statut, secteurs et intégrations."
        canonicalPath="/compare"
      />
      <div className="container py-5">
        <h1 className="display-6 fw-bold mb-2">Comparateur</h1>
        <p className="text-secondary mb-4">
          Sélectionnez jusqu&apos;à 4 produits pour comparer (cliquez sur les cartes).
        </p>

        <div className="row g-3 mb-5">
          {products.map((p) => (
            <div className="col-md-4 col-lg-3" key={p.slug}>
              <button
                type="button"
                className={`card w-100 text-start border-0 shadow-sm p-3 ${selected.includes(p.slug) ? "pep-compare-selected" : ""}`}
                onClick={() => toggle(p.slug)}
              >
                <div className="fw-semibold small">{productDisplayName(p)}</div>
                <span className={`badge ${statusBadgeClass(p.status)} mt-1`} style={{ fontSize: "0.65rem" }}>
                  {labelStatus(p.status)}
                </span>
              </button>
            </div>
          ))}
        </div>

        {compared.length >= 2 && (
          <div className="table-responsive">
            <table className="table table-bordered">
              <thead>
                <tr>
                  <th>Critère</th>
                  {compared.map((p) => (
                    <th key={p.slug}>
                      <Link to={`/products/${p.slug}`}>{productDisplayName(p)}</Link>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Statut</td>
                  {compared.map((p) => (
                    <td key={p.slug}>{labelStatus(p.status)}</td>
                  ))}
                </tr>
                <tr>
                  <td>Catégorie</td>
                  {compared.map((p) => (
                    <td key={p.slug}>{labelCategory(p.category)}</td>
                  ))}
                </tr>
                <tr>
                  <td>Self-service</td>
                  {compared.map((p) => (
                    <td key={p.slug}>
                      {p.portfolio?.trialAllowed === false
                        ? "Devis"
                        : p.trialDays
                          ? "Environnement"
                          : "—"}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td>Fonctionnalités</td>
                  {compared.map((p) => (
                    <td key={p.slug}>
                      <ul className="small mb-0 ps-3">
                        {p.features.slice(0, 5).map((f) => (
                          <li key={f}>{f}</li>
                        ))}
                      </ul>
                    </td>
                  ))}
                </tr>
                <tr>
                  <td>Intégrations</td>
                  {compared.map((p) => (
                    <td key={p.slug}>
                      <small>{p.integrations.slice(0, 5).join(", ")}</small>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}
